// TEMPORÁRIO — preview de charts para verificação visual (rota pública /preview-charts).
// Remover junto com a rota antes de finalizar.
import React, { useEffect } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../components/ui/card";
import type { ChartConfig } from "../../components/ui/chart";
import { DonutChart } from "../../components/charts/donut-chart";
import { RadialScoreChart } from "../../components/charts/radial-score-chart";
import { AreaTrendChart } from "../../components/charts/area-trend-chart";
import { RankingBarChart } from "../../components/charts/ranking-bar-chart";
import { BarTrendChart } from "../../components/charts/bar-trend-chart";
import { ChecklistAnswersChart } from "../../components/analytics/gamification/checklist-answers-chart";
import { AchievementsAdoptionList } from "../../components/analytics/gamification/achievements-adoption-list";

const adoption = [
    {
        id: 1,
        name: "O Começo de Tudo",
        description: "",
        tier: "gold",
        unlocked: 982,
        percentage: 82,
    },
    {
        id: 2,
        name: "Sonho Perfeito",
        description: "",
        tier: "silver",
        unlocked: 760,
        percentage: 64,
    },
    {
        id: 3,
        name: "Mestre dos Sonhos",
        description: "",
        tier: "gold",
        unlocked: 610,
        percentage: 51,
    },
    {
        id: 4,
        name: "Diário Consistente",
        description: "",
        tier: "bronze",
        unlocked: 512,
        percentage: 43,
    },
    {
        id: 5,
        name: "Detetive",
        description: "",
        tier: "bronze",
        unlocked: 451,
        percentage: 38,
    },
    {
        id: 6,
        name: "Artista Onírico",
        description: "",
        tier: "silver",
        unlocked: 345,
        percentage: 29,
    },
    {
        id: 7,
        name: "Planejador",
        description: "",
        tier: "bronze",
        unlocked: 262,
        percentage: 22,
    },
    {
        id: 8,
        name: "Estudante",
        description: "",
        tier: "bronze",
        unlocked: 143,
        percentage: 12,
    },
];

const ranking = adoption.map((a) => ({ label: a.name, value: a.percentage }));
const rankingConfig: ChartConfig = {
    value: { label: "Adesão", color: "hsl(var(--chart-1))" },
};

const levelData = [
    { key: "l0", value: 62 },
    { key: "l1", value: 18 },
    { key: "l2", value: 12 },
    { key: "l3", value: 8 },
];
const levelConfig: ChartConfig = {
    l0: { label: "Nível 1-5", color: "hsl(var(--chart-1))" },
    l1: { label: "Nível 6-10", color: "hsl(var(--chart-2))" },
    l2: { label: "Nível 11-15", color: "hsl(var(--chart-3))" },
    l3: { label: "Nível 16+", color: "hsl(var(--chart-4))" },
};

const hexadData = [
    { key: "Player", value: 38 },
    { key: "Achiever", value: 26 },
    { key: "Socializer", value: 14 },
    { key: "Explorer", value: 11 },
    { key: "Philanthropist", value: 7 },
    { key: "Disruptor", value: 4 },
];
const hexadConfig: ChartConfig = {
    Socializer: { label: "Socializer", color: "hsl(var(--chart-2))" },
    Philanthropist: { label: "Philanthropist", color: "hsl(var(--chart-3))" },
    Achiever: { label: "Achiever", color: "hsl(var(--chart-4))" },
    Explorer: { label: "Explorer", color: "hsl(var(--chart-5))" },
    Player: { label: "Player", color: "hsl(var(--chart-1))" },
    Disruptor: { label: "Disruptor", color: "hsl(var(--chart-6))" },
};

const accuracyConfig: ChartConfig = {
    precision: { label: "Precision@5", color: "hsl(var(--chart-1))" },
    recall: { label: "Recall@5", color: "hsl(var(--chart-2))" },
    ndcg: { label: "NDCG@5", color: "hsl(var(--chart-5))" },
};
const radial = [
    { key: "precision", value: 95 },
    { key: "recall", value: 78 },
    { key: "ndcg", value: 82 },
];
const evolution = [
    { date: "01/02", precision: 71, recall: 60, ndcg: 64 },
    { date: "01/03", precision: 76, recall: 64, ndcg: 69 },
    { date: "01/04", precision: 82, recall: 70, ndcg: 74 },
    { date: "01/05", precision: 88, recall: 74, ndcg: 79 },
    { date: "01/06", precision: 95, recall: 78, ndcg: 82 },
];

const onboarding = [
    { label: "Step 1", completed: 1180 },
    { label: "Step 2", completed: 940 },
    { label: "Step 3", completed: 720 },
    { label: "Step 5", completed: 410 },
    { label: "Step 6", completed: 263 },
];
const onboardingConfig: ChartConfig = {
    completed: { label: "Completaram", color: "hsl(var(--chart-5))" },
};

const checklist = [
    {
        questionId: 1,
        questionText: "Evitou cafeína ou estimulantes 6h antes de dormir?",
        yes: 64,
        no: 168,
        yesPercentage: 28,
    },
    {
        questionId: 2,
        questionText: "Evitou refeições pesadas e açúcar 3h antes de dormir?",
        yes: 58,
        no: 170,
        yesPercentage: 25,
    },
    {
        questionId: 3,
        questionText: "Reduziu a exposição à luz de telas antes de dormir?",
        yes: 60,
        no: 160,
        yesPercentage: 27,
    },
    {
        questionId: 4,
        questionText:
            "Fez um exercício de meditação ou relaxamento antes de dormir?",
        yes: 30,
        no: 198,
        yesPercentage: 13,
    },
    {
        questionId: 5,
        questionText: "Tentou dormir e acordar no mesmo horário?",
        yes: 72,
        no: 150,
        yesPercentage: 32,
    },
    {
        questionId: 6,
        questionText: "Dormiu por pelo menos 6 horas?",
        yes: 110,
        no: 118,
        yesPercentage: 48,
    },
    {
        questionId: 7,
        questionText: "Praticou alguma técnica de recordação de sonhos?",
        yes: 28,
        no: 200,
        yesPercentage: 12,
    },
    {
        questionId: 8,
        questionText: "Despertou de forma suave, com alarme gradual?",
        yes: 90,
        no: 138,
        yesPercentage: 39,
    },
];

const Frame: React.FC<{
    title: string;
    desc: string;
    h?: string;
    children: React.ReactNode;
}> = ({ title, desc, h = "h-[340px]", children }) => (
    <Card>
        <CardHeader>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{desc}</CardDescription>
        </CardHeader>
        <CardContent className={h}>{children}</CardContent>
    </Card>
);

export default function ChartPreview() {
    useEffect(() => {
        document.documentElement.classList.add("dark");
        return () => document.documentElement.classList.remove("dark");
    }, []);

    return (
        <div className="min-h-screen bg-background p-8 space-y-6 text-left">
            <h1 className="text-3xl font-bold text-foreground">
                Chart Preview (dark)
            </h1>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Frame
                    title="IA Accuracy — versão atual"
                    desc="Precision / Recall / NDCG @5"
                >
                    <RadialScoreChart data={radial} config={accuracyConfig} />
                </Frame>
                <Frame
                    title="Distribuição HEXAD"
                    desc="Perfil dominante da base"
                >
                    <DonutChart
                        data={hexadData}
                        config={hexadConfig}
                        centerLabel={104}
                        centerSub="usuários classificados"
                    />
                </Frame>
                <Frame
                    title="Níveis mais Populares"
                    desc="Distribuição por faixa de nível"
                >
                    <DonutChart
                        data={levelData}
                        config={levelConfig}
                        centerLabel="62%"
                        centerSub="Nível 1-5"
                    />
                </Frame>
                <Frame
                    title="Evolução das Métricas"
                    desc="Histórico Precision/Recall/NDCG"
                >
                    <AreaTrendChart
                        data={evolution}
                        config={accuracyConfig}
                        xKey="date"
                        series={[
                            { key: "precision" },
                            { key: "recall", type: "line" },
                            { key: "ndcg", type: "line", dashed: true },
                        ]}
                        showYAxis
                        showLegend
                    />
                </Frame>
                <Frame
                    title="Adesão de Conquistas (%)"
                    desc="Ranking por adesão"
                    h="h-[340px]"
                >
                    <RankingBarChart
                        data={ranking}
                        config={rankingConfig}
                        unit="%"
                        yWidth={170}
                    />
                </Frame>
                <Frame title="Onboarding por Step" desc="Completaram cada step">
                    <BarTrendChart
                        data={onboarding}
                        config={onboardingConfig}
                        xKey="label"
                        series={[{ key: "completed" }]}
                        showYAxis
                    />
                </Frame>
                <Card className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle>Respostas dos Checklists</CardTitle>
                        <CardDescription>
                            Sim vs Não por pergunta
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="h-[420px]">
                        <ChecklistAnswersChart data={checklist} />
                    </CardContent>
                </Card>
                <Card className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle>Adesão (lista/barra)</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <AchievementsAdoptionList data={adoption} />
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
